export enum LinkType {
  GOOGLE_DOC = 'google_doc',
  GOOGLE_SHEET = 'google_sheet',
  GOOGLE_SLIDE = 'google_slide',
  GOOGLE_FORM = 'google_form',
  GOOGLE_DRIVE = 'google_drive',
  PDF = 'pdf',
  GENERIC = 'generic'
}

export const ATTACHMENT_TYPE = {
  LINK: 'link',
  UPLOAD: 'upload'
} as const

export const OPEN_METHOD = {
  CARD_CLICK: 'card_click',
  MENU_OPEN: 'menu_open'
} as const

export function normalizeUrl(rawUrl: string | undefined): string | undefined {
  const trimmed = (rawUrl || '').trim()
  if (!trimmed) return undefined
  if (trimmed.startsWith('//')) return `https:${trimmed}`
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return trimmed
  return `https://${trimmed}`
}

export function getLinkType(url: string): LinkType {
  const trimmed = (url || '').trim()
  const u = trimmed.toLowerCase()
  if (!u) return LinkType.GENERIC

  if (u.includes('docs.google.com/document')) return LinkType.GOOGLE_DOC
  if (u.includes('docs.google.com/spreadsheets')) return LinkType.GOOGLE_SHEET
  if (u.includes('docs.google.com/presentation')) return LinkType.GOOGLE_SLIDE
  if (u.includes('docs.google.com/forms') || u.includes('forms.gle') || u.includes('forms.google.com'))
    return LinkType.GOOGLE_FORM
  if (u.includes('drive.google.com')) return LinkType.GOOGLE_DRIVE

  const normalized = trimmed.includes('://') ? trimmed : `https://${trimmed}`
  try {
    const parsed = new URL(normalized)
    const path = (parsed.pathname || '').toLowerCase()
    if (path.endsWith('.pdf')) return LinkType.PDF
  } catch {
    // ignore
  }

  return LinkType.GENERIC
}

const ALLOWED_URL_PROTOCOLS: ReadonlySet<string> = new Set(['http:', 'https:'])

export function validateURL(raw: string): string | null {
  const trimmed = (raw || '').trim()
  if (!trimmed) return 'URL is required'

  const candidate = normalizeUrl(trimmed)
  if (!candidate) return 'Enter a valid URL'
  try {
    const parsed = new URL(candidate)
    if (!ALLOWED_URL_PROTOCOLS.has(parsed.protocol)) {
      return 'Enter a valid URL'
    }
    const hostname = parsed.hostname
    if (!hostname || !hostname.includes('.')) {
      return 'Enter a valid URL'
    }
  } catch {
    return 'Enter a valid URL'
  }

  return null
}

export function isDuplicateUrl(url1: string, url2: string): boolean {
  try {
    const url1Obj = new URL(url1)
    const url2Obj = new URL(url2)
    // Normalize both URLs to https for comparison (treat http and https as duplicates)
    let normalizedHref1 = url1Obj.href.replace(/^http:/, 'https:')
    let normalizedHref2 = url2Obj.href.replace(/^http:/, 'https:')
    // Remove www subdomain for consistent comparison
    normalizedHref1 = normalizedHref1.replace(/^https:\/\/www\./, 'https://')
    normalizedHref2 = normalizedHref2.replace(/^https:\/\/www\./, 'https://')
    // Remove trailing slashes for consistent comparison
    normalizedHref1 = normalizedHref1.replace(/\/$/, '')
    normalizedHref2 = normalizedHref2.replace(/\/$/, '')
    return normalizedHref1 === normalizedHref2
  } catch {
    // If either URL is invalid, fall back to string comparison
    return url1 === url2
  }
}
